/**
 * domains/journal/calibration/engine.ts — MER Algo Footprints v7.1H
 *
 * Calibration Engine — single authoritative entry point.
 *
 * This is the canonical deterministic calibration logic.
 * Server-side (server/learning/calibrationEngine.js) mirrors the same
 * decision rules for runtime snapshot classification — it does NOT
 * re-implement separate business logic.
 *
 * Pipeline:
 *   classifyRecords → computeSetupFamilyMetrics → computeReadinessBandMetrics
 *   → computeThesisStateMetrics → computePenaltyEffectiveness
 *   → computeRankingFeedbackMetrics → extractAllCalibrationCandidates → report
 */

import { AuditRecord } from '../../../shared/types';
import { CalibrationReport, AttributedRecord } from './types';
import { classifyRecords }                      from './attribution';
import {
  computeSetupFamilyMetrics,
  computeReadinessBandMetrics,
  computeThesisStateMetrics,
  computePenaltyEffectiveness,
  computeRankingFeedbackMetrics,
  countOutcomeTypes,
} from './metrics';
import {
  extractAllCalibrationCandidates,
  summarizeCandidates,
} from './candidates';

function safeRate(n: number, d: number): number | null {
  return d >= 3 ? parseFloat((n / d).toFixed(3)) : null;
}

function safeAvgR(recs: AttributedRecord[]): number | null {
  const withR = recs.filter(r => r.realized_r !== null);
  if (withR.length < 3) return null;
  return parseFloat((withR.reduce((s, r) => s + r.realized_r!, 0) / withR.length).toFixed(3));
}

function safeExpectancy(recs: AttributedRecord[]): number | null {
  const withR  = recs.filter(r => r.realized_r !== null);
  if (withR.length < 3) return null;
  const wins   = withR.filter(r => r.realized_r! > 0);
  const losses = withR.filter(r => r.realized_r! < 0);
  if (!wins.length || !losses.length) return null;
  const wr = wins.length / withR.length;
  const lr = losses.length / withR.length;
  return parseFloat((
    wr * (wins.reduce((s, r) => s + r.realized_r!, 0) / wins.length) +
    lr * (losses.reduce((s, r) => s + r.realized_r!, 0) / losses.length)
  ).toFixed(3));
}

export function runCalibrationEngine(records: AuditRecord[]): CalibrationReport {
  const generated_at = Date.now();
  const EMPTY: CalibrationReport = {
    generated_at, record_count: 0, date_range: null,
    overall_win_rate: null, overall_avg_r: null, overall_expectancy: null,
    overall_false_pos_rate: null, missed_winners: 0, avoided_losses: 0,
    by_setup_family: [], by_readiness_band: [], by_thesis_state: [],
    ranking_feedback: [], penalty_metrics: [],
    calibration_candidates: [],
    candidate_summary: { threshold_miss_lower: 0, threshold_miss_upper: 0,
      false_positive_patterns: 0, false_negative_patterns: 0, over_filter: 0, under_filter: 0 },
  };

  if (!records.length) return EMPTY;

  const attributed = classifyRecords(records);
  const comp       = attributed.filter(r => r.outcome === 'win' || r.outcome === 'loss' || r.outcome === 'breakeven');
  const wns        = comp.filter(r => r.outcome === 'win');
  const fp         = attributed.filter(r => r.category === 'FALSE_POSITIVE');

  const counts     = countOutcomeTypes(attributed);
  const candidates = extractAllCalibrationCandidates(attributed, records);

  const timestamps = records.map(r => r.createdAt).sort((a, b) => a - b);

  return {
    generated_at,
    record_count:           records.length,
    date_range:             { from: timestamps[0], to: timestamps[timestamps.length - 1] },
    overall_win_rate:       safeRate(wns.length, comp.length),
    overall_avg_r:          safeAvgR(comp),
    overall_expectancy:     safeExpectancy(comp),
    overall_false_pos_rate: safeRate(fp.length, Math.max(1, comp.length)),
    missed_winners:         counts.missed_winners,
    avoided_losses:         counts.avoided_losses,
    by_setup_family:        computeSetupFamilyMetrics(attributed),
    by_readiness_band:      computeReadinessBandMetrics(attributed),
    by_thesis_state:        computeThesisStateMetrics(attributed),
    ranking_feedback:       computeRankingFeedbackMetrics(attributed),
    penalty_metrics:        computePenaltyEffectiveness(attributed),
    calibration_candidates: candidates,
    candidate_summary:      summarizeCandidates(candidates),
  };
}

export { classifyRecord }  from './attribution';
export { classifyRecords } from './attribution';
